import { inject } from "@angular/core";
import { ResolveFn } from "@angular/router";
import { catchError, finalize, map, of, tap } from "rxjs";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import {
  describeApiRequestFailed,
  describeRouteEntered,
  extractSupportErrorMessage,
} from "src/app/shared/helpers/contact-support-history.helper";
import { ViewContextHistoryStateService } from "src/app/shared/services/view-context-history-state.service";
import { BillingServiceOverviewStore } from "../billing-status-widget/billing-service-overview.store";
import {
  BillingServiceOverview,
} from "../billing-status-widget/billing-service-overview.model";

export interface UsageOverviewResolverData {
  overview: BillingServiceOverview | null;
  errorMessage: string | null;
}

/** Loads billing service overview before activating the usage overview page. */
export const usageOverviewResolver: ResolveFn<UsageOverviewResolverData> = (route) => {
  const overviewStore = inject(BillingServiceOverviewStore);
  const loaderService = inject(LoaderService);
  const viewContextHistorySvc = inject(ViewContextHistoryStateService);

  loaderService.show();

  return overviewStore.load().pipe(
    map(() => ({
      overview: overviewStore.overview(),
      errorMessage: overviewStore.errorMessage(),
    })),
    tap((resolvedData) => {
      viewContextHistorySvc.createContext({
        titleView: "Usage Overview",
        eventAction: "route_entered",
        eventDescription: describeRouteEntered("Usage Overview"),
        thisState: resolvedData,
        route,
        setAutomaticallyScreenshot: false,
      });
    }),
    catchError((error) => {
      console.error("Error resolving usage overview:", error);

      viewContextHistorySvc.createContext({
        titleView: "Usage Overview",
        eventAction: "data_fetch_failed",
        eventDescription: describeApiRequestFailed(
          "load usage overview in resolver",
          extractSupportErrorMessage(error),
          { status: error.status },
        ),
        thisState: {
          error: { message: error.message, status: error.status },
        },
        route,
        setAutomaticallyScreenshot: false,
      });

      return of({
        overview: null,
        errorMessage: extractSupportErrorMessage(error),
      });
    }),
    finalize(() => loaderService.hide()),
  );
};
